import { ref, computed, watch, onMounted } from 'vue'
import dayjs from 'dayjs'
import useAppStore from '@/store/modules/app'
import { getSpotIncomeStat } from '@/api/spot'

/** 颗粒度选项 */
export const SPOT_GRANULARITIES = [
  { value: '15min', label: '15分钟' },
  { value: 'hour', label: '小时' },
  { value: 'day', label: '天' },
]

/**
 * 各颗粒度允许的最大日期跨度（天）。
 * 15min 一天 96 格，不限制的话跨月会拉出近 3000 行，ECharts 和 el-table 都扛不住。
 * null 表示不限制。
 */
export const SPOT_MAX_SPAN_DAYS = {
  '15min': 7,
  hour: 60,
  day: null,
}

/** 空值口径：null / undefined / '' / NaN 一律视为缺失，不能退化成 0 */
export function isBlank(value) {
  if (value === null || value === undefined || value === '') return true
  return Number.isNaN(Number(value))
}

/** 缺失返回 null，否则返回数字。用于图表 —— ECharts 对 null 天然断点 */
export function toNum(value) {
  return isBlank(value) ? null : Number(value)
}

/** 求和时跳过缺失值，全缺失返回 null 而非 0 */
export function sumSkipBlank(rows, key) {
  let has = false
  let total = 0
  for (const row of rows) {
    const v = toNum(row?.[key])
    if (v === null) continue
    has = true
    total += v
  }
  return has ? total : null
}

/**
 * 把接口返回行加工成图表/表格通用结构。
 * 只补充派生字段，不改动原始字段，避免下游误判缺价。
 */
export function normalizeSpotRows(rows, granularity) {
  if (!Array.isArray(rows)) return []
  return rows.map((row, index) => {
    const settleDate = row?.settleDate ?? ''
    const timeSlot = row?.timeSlot ?? ''
    // day 颗粒度 timeSlot 为 null，标签就是日期本身
    const label = granularity === 'day' || !timeSlot
      ? settleDate
      : `${settleDate} ${timeSlot}`
    const slotTotal = Number(row?.slotTotal) || 0
    const slotPricedRt = Number(row?.slotPricedRt) || 0
    const slotPricedDa = Number(row?.slotPricedDa) || 0
    return {
      ...row,
      _key: `${settleDate}-${timeSlot}-${row?.uuid ?? ''}-${index}`,
      _label: label,
      // 短标签：跨度内同一天的格子只在第一格显示日期，x 轴用
      _shortLabel: granularity === 'day' ? settleDate : (timeSlot || settleDate),
      _missingRt: Math.max(0, slotTotal - slotPricedRt),
      _missingDa: Math.max(0, slotTotal - slotPricedDa),
    }
  })
}

/**
 * 现货结算共用状态机与取数。
 *
 * 供「收益总览」和「抄表结算」两个 Tab 复用：口径切换、颗粒度、防竞态取数、
 * 以及 bit5 三态的初始化规则都收在这里，避免两处各写一遍走样。
 *
 * @param {object} [options]
 * @param {'15min'|'hour'|'day'} [options.defaultGranularity='15min']
 */
export function useSpotSettlement(options = {}) {
  const { defaultGranularity = '15min' } = options
  const appStore = useAppStore()

  /** 三态：true 开启 / false 关闭 / undefined 尚不确定（不渲染切换器） */
  const spotEnabled = computed(() => appStore.spotSettlementEnabled)

  /** 'meter' 抄表结算（原有逻辑） | 'spot' 现货结算 */
  const settleMode = ref('meter')
  const granularity = ref(defaultGranularity)

  const rows = ref([])
  const loading = ref(false)
  /** 最近一次请求的错误信息，供 UI 展示空态 */
  const error = ref('')

  // 用户手动切过之后不再被自动初始化覆盖
  let userTouched = false
  function setSettleMode(mode) {
    userTouched = true
    settleMode.value = mode
  }

  // bit5 落定后的默认口径：
  // - bit5=true（开启现货）：仍默认显示抄表，让用户主动切到现货
  // - bit5=false（未开启）：强制回到抄表，避免残留现货视图
  watch(spotEnabled, (enabled) => {
    if (enabled === true) {
      // 开启现货的电站，默认仍停留在抄表结算（用户需要手动切换）
      if (!userTouched) settleMode.value = 'meter'
    } else if (enabled === false) {
      // 切到未开启现货的电站，必须回到抄表，否则会残留现货视图
      settleMode.value = 'meter'
      userTouched = false
    }
  }, { immediate: true })

  // 切换电站时重置，避免把上一个电站的数据和用户选择带过来
  watch(() => appStore.stationId, (id) => {
    userTouched = false
    rows.value = []
    error.value = ''
    granularity.value = defaultGranularity
    if (id) appStore.getBaseData(id)
  })

  /**
   * 进入 Tab 时主动刷新电站基础信息。
   *
   * deviceFlag 由 StationStatusBar 在挂载时拉取，但同一电站内切 Tab 时它不会重新挂载
   * （Navbar 的 v-if 只看 URL 有没有 stationId），stationId 也没变，watch 同样不触发。
   * 结果是在电站管理里刚勾选的能力位，回到本 Tab 仍读到 localStorage 里的旧值。
   * 这里主动拉一次，store 内部有在途去重，不会与 StationStatusBar 重复请求。
   */
  onMounted(() => {
    if (appStore.stationId) appStore.getBaseData(appStore.stationId)
  })

  const isSpot = computed(() => spotEnabled.value === true && settleMode.value === 'spot')

  /** 当前颗粒度允许的最大跨度（天），null 表示不限 */
  const maxSpanDays = computed(() => SPOT_MAX_SPAN_DAYS[granularity.value] ?? null)

  /**
   * 校验日期跨度是否超出当前颗粒度的上限
   * @returns {{ ok: boolean, message?: string }}
   */
  function validateSpan(startDate, endDate) {
    const limit = maxSpanDays.value
    if (limit == null) return { ok: true }
    if (!startDate || !endDate) return { ok: true }
    // 含首尾，所以 +1
    const span = dayjs(endDate).diff(dayjs(startDate), 'day') + 1
    if (span > limit) {
      const label = SPOT_GRANULARITIES.find(g => g.value === granularity.value)?.label || granularity.value
      return { ok: false, message: `${label}颗粒度下日期跨度不能超过 ${limit} 天，当前为 ${span} 天` }
    }
    return { ok: true }
  }

  // 请求序号，丢弃过期响应（同 business-operations/summary 的 fetchStationListReqSeq 模式）
  let reqSeq = 0

  /**
   * 拉取现货统计
   * @param {object} p
   * @param {string} p.startDate yyyy-MM-dd
   * @param {string} p.endDate   yyyy-MM-dd
   * @param {string} [p.uuid]    电表 uuid，'all' 或空表示整站汇总
   * @param {boolean} [p.byMeter]
   */
  async function fetchSpotStat({ startDate, endDate, uuid, byMeter } = {}) {
    const stationId = appStore.stationId
    if (!stationId || !startDate || !endDate) {
      rows.value = []
      return []
    }
    const span = validateSpan(startDate, endDate)
    if (!span.ok) {
      rows.value = []
      error.value = span.message
      return []
    }

    const seq = ++reqSeq
    loading.value = true
    error.value = ''
    try {
      const params = {
        stationId,
        startDate,
        endDate,
        granularity: granularity.value,
      }
      if (uuid && uuid !== 'all') params.uuid = uuid
      else if (byMeter) params.byMeter = true

      const res = await getSpotIncomeStat(params)
      if (seq !== reqSeq) return rows.value // 过期响应，丢弃

      const list = Array.isArray(res?.data) ? res.data : []
      rows.value = normalizeSpotRows(list, granularity.value)
      return rows.value
    } catch (e) {
      if (seq === reqSeq) {
        console.error('获取现货收益统计失败:', e)
        error.value = '获取现货收益统计失败'
        rows.value = []
      }
      return []
    } finally {
      if (seq === reqSeq) loading.value = false
    }
  }

  /** 区间汇总，供汇总卡片与完整性面板使用 */
  const summary = computed(() => {
    const list = rows.value
    const slotTotal = list.reduce((acc, r) => acc + (Number(r.slotTotal) || 0), 0)
    const slotPricedRt = list.reduce((acc, r) => acc + (Number(r.slotPricedRt) || 0), 0)
    const slotPricedDa = list.reduce((acc, r) => acc + (Number(r.slotPricedDa) || 0), 0)
    const realtimeIncome = sumSkipBlank(list, 'realtimeIncome')
    const dayaheadIncome = sumSkipBlank(list, 'dayaheadIncome')
    return {
      chargeEnergy: sumSkipBlank(list, 'chargeEnergy'),
      dischargeEnergy: sumSkipBlank(list, 'dischargeEnergy'),
      netEnergy: sumSkipBlank(list, 'netEnergy'),
      realtimeIncome,
      dayaheadIncome,
      // 实时价相对日前价的收益差额，两者任一缺失则为 null
      incomeDiff: (realtimeIncome === null || dayaheadIncome === null)
        ? null
        : realtimeIncome - dayaheadIncome,
      slotTotal,
      slotPricedRt,
      slotPricedDa,
      rtCoverage: slotTotal > 0 ? slotPricedRt / slotTotal : null,
      daCoverage: slotTotal > 0 ? slotPricedDa / slotTotal : null,
      incompleteRtCount: list.filter(r => r.realtimeComplete === false).length,
      incompleteDaCount: list.filter(r => r.dayaheadComplete === false).length,
      meterCount: list.reduce((acc, r) => Math.max(acc, Number(r.meterCount) || 0), 0),
      rowCount: list.length,
    }
  })

  return {
    spotEnabled,
    settleMode,
    setSettleMode,
    granularity,
    isSpot,
    rows,
    loading,
    error,
    summary,
    maxSpanDays,
    validateSpan,
    fetchSpotStat,
  }
}
